import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  ErrorState,
  Field,
  InlineAlert,
  Input,
  LoadingState,
  Select,
  StatusBadge,
  Textarea,
} from "../../shared/ui";
import type {
  SprintCarryoverReason,
  SprintDetail,
  SprintSummary,
  UnfinishedItem,
} from "../../shared/types/api";
import {
  CARRYOVER_REASONS,
  carryoverNeedsComment,
  describeCarryover,
} from "./sprintVocabulary";

/** The destination meaning "make a sprint for this", resolved once on save. */
const NEW_SPRINT = "__new__";

/**
 * The backlog, named explicitly for the bulk control.
 *
 * <p>An empty value already means the backlog on each item's own selector, where the blank option
 * is the default. The bulk control needs its blank for "choose one…", so it cannot use the same
 * value for both without one of the two silently doing nothing.
 */
const BACKLOG = "__backlog__";

/**
 * Closing a sprint, item by item.
 *
 * <p>The whole point of this panel is the middle of it. A sprint used to close in one click,
 * count its carryover, and leave the unfinished work inside a sprint nobody opens again — in no
 * backlog, in no sprint, on no list. Here every item that did not land is named, has to be given
 * a reason, and is sent somewhere on purpose.
 *
 * <p>Somewhere, not everywhere: each item gets its own destination. Two items missing the same
 * sprint are rarely the same decision — one is next up and the other has just been overtaken by
 * something more important — and a panel that moved them together would force the person closing
 * to lie about one of them.
 *
 * <p>No default reason is pre-selected. A default is a reason nobody gave, and it would fill the
 * retrospective with rows saying "not started" about work that sat in QA for a week.
 */
export function CompleteSprintDrawer({
  sprint,
  onClose,
  onSaved,
}: {
  sprint: SprintSummary | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reasons, setReasons] = useState<
    Record<string, SprintCarryoverReason | "">
  >({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [destinations, setDestinations] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState("");
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const unfinished = useQuery({
    queryKey: ["sprint-unfinished", sprint?.id],
    queryFn: () =>
      api.get<UnfinishedItem[]>(`/api/v1/sprints/${sprint?.id}/unfinished`),
    enabled: Boolean(sprint),
  });

  /** Where the work could go: any sprint still open, other than the one being closed. */
  const sprints = useQuery({
    queryKey: ["sprints", sprint?.projectId],
    queryFn: () =>
      api.get<SprintSummary[]>(
        `/api/v1/sprints?projectId=${sprint?.projectId}`,
      ),
    enabled: Boolean(sprint),
  });

  const open = (sprints.data ?? []).filter(
    (candidate) =>
      candidate.id !== sprint?.id &&
      (candidate.status === "PLANNED" || candidate.status === "ACTIVE"),
  );

  useEffect(() => {
    if (!sprint) {
      return;
    }
    setReasons({});
    setComments({});
    setDestinations({});
    setError(null);

    // The obvious next sprint: starts the day after this one ends, and runs the usual fortnight.
    const nextSequence = (sprints.data?.length ?? 0) + 1;
    const start = addDays(sprint.endDate, 1);
    setNewName(`Sprint ${nextSequence}`);
    setNewStart(start);
    setNewEnd(addDays(start, 11));
  }, [sprint, sprints.data]);

  const items = unfinished.data ?? [];
  const missing = items.filter((item) => !reasons[item.workItemId]);
  const unexplained = items.filter(
    (item) =>
      reasons[item.workItemId] &&
      carryoverNeedsComment(
        reasons[item.workItemId] as SprintCarryoverReason,
      ) &&
      !(comments[item.workItemId] ?? "").trim(),
  );
  const wantsNewSprint = items.some(
    (item) => destinations[item.workItemId] === NEW_SPRINT,
  );
  const newSprintIncomplete =
    wantsNewSprint &&
    (newName.trim() === "" || newStart === "" || newEnd === "");
  const backwards = Boolean(newStart && newEnd) && newEnd < newStart;

  const ready =
    missing.length === 0 &&
    unexplained.length === 0 &&
    !newSprintIncomplete &&
    !backwards;

  /** Sets every item's destination at once, for the common case where they all go together. */
  const sendAllTo = (value: string) => {
    if (value === "") {
      return;
    }
    const destination = value === BACKLOG ? "" : value;
    setDestinations(
      Object.fromEntries(items.map((item) => [item.workItemId, destination])),
    );
  };

  const submit = async () => {
    if (!sprint) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      /*
       * The sprint is made first, and made empty. carryOverWaitingItems is deliberately false:
       * this sprint is for the items chosen below, and letting it also sweep up everything else
       * waiting in the backlog would quietly plan work nobody in this conversation mentioned.
       */
      let created: string | null = null;
      if (wantsNewSprint) {
        const madeSprint = await api.post<SprintDetail>("/api/v1/sprints", {
          projectId: sprint.projectId,
          name: newName,
          goal: null,
          teamId: sprint.teamId,
          startDate: newStart,
          endDate: newEnd,
          carryOverWaitingItems: false,
        });
        created = madeSprint.sprint.id;
      }

      await api.post(`/api/v1/sprints/${sprint.id}/complete`, {
        carryover: items.map((item) => {
          const destination = destinations[item.workItemId] ?? "";
          return {
            workItemId: item.workItemId,
            reason: reasons[item.workItemId],
            comment: (comments[item.workItemId] ?? "").trim() || null,
            moveToSprintId:
              destination === NEW_SPRINT ? created : destination || null,
          };
        }),
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not close the sprint.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(sprint)}
      onClose={onClose}
      title="Complete this sprint"
      subtitle={sprint?.name}
      width="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-ink-500">
            {missing.length > 0
              ? `${missing.length} still to account for`
              : "The figures are fixed once this is done."}
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Not yet
            </Button>
            <Button
              onClick={() => void submit()}
              loading={saving}
              disabled={!ready}
            >
              {saving ? "Closing…" : "Complete sprint"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {unfinished.isPending && (
          <LoadingState label="Checking what is left…" />
        )}
        {unfinished.isError && (
          <ErrorState
            error={unfinished.error}
            onRetry={() => void unfinished.refetch()}
          />
        )}

        {unfinished.isSuccess && (
          <>
            <InlineAlert tone={items.length === 0 ? "success" : "warning"}>
              {describeCarryover(
                items.length,
                sprint?.itemCount ?? items.length,
              )}
            </InlineAlert>

            {items.length > 1 && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-ink-200 bg-ink-50/60 px-3 py-2">
                <span className="text-sm text-ink-700">Send all to</span>
                <div className="min-w-56 flex-1">
                  <Select
                    value=""
                    onChange={(event) => sendAllTo(event.target.value)}
                  >
                    <option value="">Choose for every item…</option>
                    <option value={BACKLOG}>Back to the backlog</option>
                    {open.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name}
                        {candidate.status === "ACTIVE" ? " (running)" : ""}
                      </option>
                    ))}
                    <option value={NEW_SPRINT}>＋ A new sprint</option>
                  </Select>
                </div>
                <span className="text-xs text-ink-500">
                  Each item can still be changed below.
                </span>
              </div>
            )}

            {items.length > 0 && (
              <p className="text-sm text-ink-600">
                Each of these is tagged as undone from{" "}
                <span className="font-medium text-ink-800">{sprint?.name}</span>{" "}
                and goes where you send it. Left in the backlog, it waits there
                — visibly — until somebody plans it in.
              </p>
            )}

            <div className="space-y-3">
              {items.map((item) => {
                const reason = reasons[item.workItemId] ?? "";
                const needsComment =
                  reason !== "" &&
                  carryoverNeedsComment(reason as SprintCarryoverReason);
                return (
                  <div
                    key={item.workItemId}
                    className="rounded-lg border border-ink-200 bg-white p-3"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-ink-900">
                          <span className="font-mono text-xs text-ink-500">
                            {item.itemKey}
                          </span>{" "}
                          {item.title}
                        </p>
                        <p className="mt-0.5 text-xs text-ink-500">
                          {item.assigneeName ?? "Unassigned"}
                          {item.storyPoints != null &&
                            ` · ${item.storyPoints} pts`}
                        </p>
                      </div>
                      {!item.stillInSprint && (
                        <StatusBadge tone="amber">
                          Taken out mid-sprint
                        </StatusBadge>
                      )}
                    </div>

                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <Select
                        value={reason}
                        onChange={(event) =>
                          setReasons((current) => ({
                            ...current,
                            [item.workItemId]: event.target
                              .value as SprintCarryoverReason,
                          }))
                        }
                      >
                        <option value="">Why did it not land?</option>
                        {CARRYOVER_REASONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                      <Textarea
                        rows={1}
                        value={comments[item.workItemId] ?? ""}
                        onChange={(event) =>
                          setComments((current) => ({
                            ...current,
                            [item.workItemId]: event.target.value,
                          }))
                        }
                        placeholder={
                          needsComment
                            ? "Required: say what actually happened"
                            : "Anything worth knowing at the retrospective"
                        }
                      />
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-ink-500">Goes to</span>
                      <div className="min-w-56 flex-1">
                        <Select
                          value={destinations[item.workItemId] ?? ""}
                          onChange={(event) =>
                            setDestinations((current) => ({
                              ...current,
                              [item.workItemId]: event.target.value,
                            }))
                          }
                        >
                          <option value="">Back to the backlog</option>
                          {open.map((candidate) => (
                            <option key={candidate.id} value={candidate.id}>
                              {candidate.name}
                              {candidate.status === "ACTIVE"
                                ? " (running)"
                                : ""}
                            </option>
                          ))}
                          <option value={NEW_SPRINT}>＋ A new sprint</option>
                        </Select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/*
             * Asked for only once something is actually going into it, and created empty: a
             * sprint made here is for the items chosen above and nothing else.
             */}
            {wantsNewSprint && (
              <div className="space-y-3 rounded-lg border border-brand-200 bg-brand-50/50 p-3">
                <p className="text-sm font-medium text-ink-900">
                  The new sprint
                </p>
                <Field label="Name" required>
                  <Input
                    value={newName}
                    onChange={(event) => setNewName(event.target.value)}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Starts" required>
                    <Input
                      type="date"
                      value={newStart}
                      onChange={(event) => setNewStart(event.target.value)}
                    />
                  </Field>
                  <Field label="Ends" required>
                    <Input
                      type="date"
                      value={newEnd}
                      onChange={(event) => setNewEnd(event.target.value)}
                    />
                  </Field>
                </div>
                {backwards && (
                  <InlineAlert tone="warning">
                    A sprint cannot end before it starts.
                  </InlineAlert>
                )}
                <p className="text-xs text-ink-600">
                  It starts planned and empty apart from what you send it here,
                  so nothing is committed until somebody starts it.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}

/** Days added to an ISO date, in UTC so a daylight-saving change cannot shift the answer. */
function addDays(date: string, days: number): string {
  const parsed = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed)) {
    return date;
  }
  return new Date(parsed + days * 86_400_000).toISOString().slice(0, 10);
}
